import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react'

interface CreditContextType {
  totalCredits: number
  currentGameCredits: number
  displayTotalCredits: number
  displayGameCredits: number
  isAnimating: boolean
  deductCredits: (amount: number) => void
  updateCredits: (amount: number) => void
  setInitialCredits: (amount: number) => void
  resetGameCredits: () => void
}

const CreditContext = createContext<CreditContextType | undefined>(undefined)

export function CreditProvider({ children }: { children: ReactNode }) {
  const [INITIAL_CREDITS, setINITIAL_CREDITS] = useState(0)
  const [totalCredits, setTotalCredits] = useState(INITIAL_CREDITS)
  const [currentGameCredits, setCurrentGameCredits] = useState(0)
  const [displayTotalCredits, setDisplayTotalCredits] = useState(INITIAL_CREDITS)
  const [displayGameCredits, setDisplayGameCredits] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  const setInitialCredits = useCallback((initialBusinessCreditCount: number) => {
    setINITIAL_CREDITS(initialBusinessCreditCount)
    setTotalCredits(initialBusinessCreditCount)
    setDisplayTotalCredits(initialBusinessCreditCount)
  }, [])

  // Animate credit changes
  useEffect(() => {
    if (displayTotalCredits === totalCredits && displayGameCredits === currentGameCredits) {
      setIsAnimating(false)
      return
    }

    setIsAnimating(true)
    const duration = 1500 // 1.5 seconds
    const steps = 60
    const interval = duration / steps

    const totalDiff = totalCredits - displayTotalCredits
    const gameDiff = currentGameCredits - displayGameCredits
    const totalStep = totalDiff / steps
    const gameStep = gameDiff / steps

    let currentStep = 0

    const timer = setInterval(() => {
      currentStep++

      if (currentStep >= steps) {
        setDisplayTotalCredits(totalCredits)
        setDisplayGameCredits(currentGameCredits)
        setIsAnimating(false)
        clearInterval(timer)
      } else {
        setDisplayTotalCredits((prev) => Math.round(prev + totalStep))
        setDisplayGameCredits((prev) => Math.round(prev + gameStep))
      }
    }, interval)

    return () => clearInterval(timer)
  }, [totalCredits, currentGameCredits, displayTotalCredits, displayGameCredits])

  const deductCredits = useCallback((amount: number) => {
    const newTotalCredits = Math.max(0, totalCredits - amount) 
    const newGameCredits = currentGameCredits + amount
    setTotalCredits(newTotalCredits)
    setCurrentGameCredits(newGameCredits)
  }, [totalCredits, currentGameCredits])

  const updateCredits =(totalConsumedAmoount: number) => {
    setTotalCredits(INITIAL_CREDITS - totalConsumedAmoount)
    setCurrentGameCredits(totalConsumedAmoount)
  }

  const resetGameCredits = () => {
    setCurrentGameCredits(0)
  }

  return (
    <CreditContext.Provider
      value={{
        totalCredits,
        currentGameCredits,
        displayTotalCredits,
        displayGameCredits,
        isAnimating,
        deductCredits,
        updateCredits,
        resetGameCredits,
        setInitialCredits,
      }}
    >
      {children}
    </CreditContext.Provider>
  )
}

export function useCredits() {
  const context = useContext(CreditContext)
  if (!context) {
    throw new Error('useCredits must be used within CreditProvider')
  }
  return context
}
